import type { UserQuestionAnswer } from '@/frontend/pages/Dashboard/AgentChats/types'
import React, { useCallback, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import type { ProcedurePresentation } from '@/frontend/features/AgentChats/events/agentEvents'
import type { AgentChatDigitalWorker } from '@/frontend/pages/Dashboard/AgentChats/types'
import type { useAgentChatController } from '@/frontend/pages/Dashboard/AgentChats/useAgentChatController'
import { ProcedureDraftCanvas } from './ProcedureDraftCanvas'

const AssistantConversationPanel = React.lazy(() =>
  import('@/frontend/features/AgentChats/assistant-ui/components/AssistantConversationPanel')
    .then((module) => ({ default: module.AssistantConversationPanel })),
)

interface Props {
  chat: ReturnType<typeof useAgentChatController>
  worker: AgentChatDigitalWorker
  userName: string | null
  procedure: ProcedurePresentation | null
  showProcedureCanvas: boolean
}

export function BusinessProcessConversationWorkspace({
  chat,
  worker,
  userName,
  procedure,
  showProcedureCanvas,
}: Props) {
  const [mobileView, setMobileView] = useState<'process' | 'conversation'>('conversation')
  const description = useMemo(() => {
    const firstMessage = chat.session?.messages.find((message) => message.role === 'user')?.content || ''
    let normalized = firstMessage.trim()
    let previous = ''
    while (normalized !== previous) {
      previous = normalized
      normalized = normalized
        .replace(/^(?:quero\s+)?criar\s+um\s+novo\s+(?:processo|procedimento)\s*:\s*/i, '')
        .trim()
    }
    return normalized
  }, [chat.session?.messages])
  const submitMessage = useCallback((content: string, files: File[], onRecoveredCommit?: () => void, questionAnswer?: UserQuestionAnswer) => (
    chat.submitMessage(content, files, undefined, onRecoveredCommit, questionAnswer)
  ), [chat.submitMessage])

  const conversationPanel = (
    <React.Suspense
      fallback={
        <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
          Carregando conversa…
        </div>
      }
    >
      <AssistantConversationPanel
        variant={showProcedureCanvas ? 'procedure' : 'widget'}
        error={chat.visibleError}
        attachmentError={chat.attachmentError}
        activity={chat.activity}
        isRunning={chat.assistantIsRunning}
        connectionStatus={chat.connectionStatus}
        digitalWorker={worker}
        session={chat.session}
        currentUserName={userName}
        headerSubtitle={showProcedureCanvas ? 'Construindo um novo processo' : null}
        onAttachmentError={chat.setAttachmentError}
        onStop={chat.abortCurrentTurn}
        onRetry={chat.retryFailedTurn}
        onFeedback={chat.submitMessageFeedback}
        onBranch={chat.branchConversation}
        onSubmit={submitMessage}
      />
    </React.Suspense>
  )

  if (!showProcedureCanvas) {
    return (
      <section
        data-testid="novo-processo-conversa"
        data-surface="business-process-conversation"
        className="mx-auto flex h-full min-h-0 w-full min-w-0 max-w-[96rem] overflow-hidden bg-background"
      >
        {conversationPanel}
      </section>
    )
  }

  return (
    <section
      data-testid="novo-processo-conversa"
      data-surface="business-process-workspace"
      className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-background lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(380px,460px)]"
    >
      <div className="flex h-11 shrink-0 items-center justify-center gap-1 border-b border-border px-3 lg:hidden">
        {(['process', 'conversation'] as const).map((view) => (
          <button
            key={view}
            type="button"
            onClick={() => setMobileView(view)}
            aria-pressed={mobileView === view}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              mobileView === view ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {view === 'process' ? 'Processo' : 'Conversa'}
          </button>
        ))}
      </div>

      <div className={cn('min-h-0 overflow-hidden', mobileView === 'process' ? 'flex' : 'hidden', 'lg:flex')}>
        <ProcedureDraftCanvas
          procedure={procedure}
          description={description}
          running={chat.assistantIsRunning}
          error={chat.visibleError}
        />
      </div>

      <aside className={cn(
        'min-h-0 overflow-hidden border-l-0 border-border lg:flex lg:border-l',
        mobileView === 'conversation' ? 'flex' : 'hidden',
      )}>
        {conversationPanel}
      </aside>
    </section>
  )
}
